import { queryOptions } from "@tanstack/react-query";

import { api } from "@/api/client";
import type {
  ApproveCardsResponse,
  BankStatementApproveResponse,
  BankStatementRejectReasonKey,
  BankStatementRejectResponse,
  BankStatementsResponse,
  CardStatementRejectReasonKey,
  FraudRestrictionsResponse,
  IgnoreRestrictionResponse,
  RejectCardStatementsResponse,
  ReviewQueueSummary,
  WithdrawalRail,
} from "@/api/types";
import { QUEUE_LIST_OPTIONS } from "./kyc";
import { REVIEW_SUMMARY_KEY, useQueueMutation } from "./queue-mutation";

export const REVIEWS_KEY = ["reviews"] as const;
export const FRAUD_RESTRICTIONS_KEY = [
  ...REVIEWS_KEY,
  "fraud-restrictions",
] as const;
export const bankStatementsKey = (status: "pending" | "rejected") =>
  [...REVIEWS_KEY, "bank-statements", status] as const;

/** One list feeds both the fraud-scoring and the nationality tab. */
export const fraudRestrictionsQuery = () =>
  queryOptions({
    queryKey: FRAUD_RESTRICTIONS_KEY,
    queryFn: () =>
      api.get<FraudRestrictionsResponse>("/admin/fraud-restrictions"),
    ...QUEUE_LIST_OPTIONS,
  });

export const bankStatementsQuery = (
  status: "pending" | "rejected" = "pending",
) =>
  queryOptions({
    queryKey: bankStatementsKey(status),
    queryFn: () =>
      api.get<BankStatementsResponse>(
        `/admin/bank-statements?status=${status}`,
      ),
    ...QUEUE_LIST_OPTIONS,
  });

/** Sidebar badges: one tiny request a minute; keys appear per permission. */
export const reviewQueueSummaryQuery = () =>
  queryOptions({
    queryKey: REVIEW_SUMMARY_KEY,
    queryFn: () => api.get<ReviewQueueSummary>("/admin/review-queues/summary"),
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });

// ---------------------------------------------------------------------------
// Fraud / nationality restriction actions (card-verifications.ts)
// ---------------------------------------------------------------------------

export interface CardSelectionInput {
  userId: string;
  restrictionId: string;
  walletsLoadIds: string[] | null;
  cardAuthVerificationIds: string[] | null;
  statementCards:
    | { last4: string; paymentMethodType: string; bin: string | null }[]
    | null;
}

/**
 * Approve takes cards and payout accounts together — one decision, one
 * re-score. Reject stays cards-only: a payout account has no statement to
 * decline.
 */
export type ApproveSelectionInput = CardSelectionInput & {
  withdrawalDestinations:
    | { rail: WithdrawalRail; destinationId: string }[]
    | null;
};

export const useApproveCards = () =>
  useQueueMutation(
    [FRAUD_RESTRICTIONS_KEY],
    (input: ApproveSelectionInput) =>
      api.post<ApproveCardsResponse>(
        "/admin/card-verifications/approve",
        input,
      ),
    { quietErrors: () => true },
  );

export const useRejectCardStatements = () =>
  useQueueMutation(
    [FRAUD_RESTRICTIONS_KEY],
    (input: CardSelectionInput & { reason: CardStatementRejectReasonKey }) =>
      api.post<RejectCardStatementsResponse>(
        "/admin/card-verifications/reject",
        input,
      ),
    { quietErrors: () => true },
  );

export const useIgnoreRestriction = () =>
  useQueueMutation(
    [FRAUD_RESTRICTIONS_KEY],
    (input: { restrictionId: string; reason: string }) =>
      api.post<IgnoreRestrictionResponse>(
        "/admin/fraud-restrictions/ignore",
        input,
      ),
    { quietErrors: () => true },
  );

// ---------------------------------------------------------------------------
// Bank statement actions (bank-statements.ts)
// ---------------------------------------------------------------------------

export const useApproveBankStatement = () =>
  useQueueMutation(
    [bankStatementsKey("pending")],
    (input: { submissionId: string }) =>
      api.post<BankStatementApproveResponse>(
        `/admin/bank-statements/${encodeURIComponent(input.submissionId)}/approve`,
      ),
    { quietErrors: () => true },
  );

export const useRejectBankStatement = () =>
  useQueueMutation(
    [bankStatementsKey("pending")],
    (input: {
      submissionId: string;
      reasonKey: BankStatementRejectReasonKey;
    }) =>
      api.post<BankStatementRejectResponse>(
        `/admin/bank-statements/${encodeURIComponent(input.submissionId)}/reject`,
        { reasonKey: input.reasonKey },
      ),
    { quietErrors: () => true },
  );
